import type { FolderRow } from "@loumy/contracts";

// §11/D100 — reine Folder-Tree-Logik (React-frei, unit-getestet): Baum aus der flachen
// Ordner-Liste + Descendant-Guard für Folder-Drag&Drop (Zyklus-UX clientseitig; der
// harte Schutz ist der folders_no_cycle-Trigger serverseitig, D40).

export interface FolderTreeNode extends FolderRow {
  children: FolderTreeNode[];
}

/** Flache §9.3-Zeilen → Baum (parent_id); verwaiste Parents (nicht geladen) → Root. */
export function buildTree(rows: FolderRow[]): FolderTreeNode[] {
  const byId = new Map<string, FolderTreeNode>();
  rows.forEach((r) => byId.set(r.id, { ...r, children: [] }));
  const roots: FolderTreeNode[] = [];
  byId.forEach((node) => {
    const parent = node.parent_id ? byId.get(node.parent_id) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });
  return roots;
}

/**
 * Ist `targetId` der Ordner `folderId` selbst oder ein Nachfahre davon? (Dann ist ein
 * Folder-Move dorthin ein Zyklus → Drop ablehnen.) Läuft die parent_id-Kette des Ziels
 * hoch; ein Besucht-Set fängt defensiv korrupte Zyklen in den Daten ab.
 */
export function isSelfOrDescendant(rows: FolderRow[], folderId: string, targetId: string | null): boolean {
  if (targetId === null) return false; // oberste Ebene ist nie Teil eines Subtrees
  const parentById = new Map(rows.map((r) => [r.id, r.parent_id] as const));
  const seen = new Set<string>();
  let cur: string | null = targetId;
  while (cur !== null) {
    if (cur === folderId) return true;
    if (seen.has(cur)) return false;
    seen.add(cur);
    cur = parentById.get(cur) ?? null;
  }
  return false;
}

// DnD-Payload-Typen (custom MIME — in dragover sind nur `types` lesbar, das genügt zur
// Ziel-Hervorhebung; die ID selbst kommt erst im drop über getData).
export const ASSET_DRAG_TYPE = "application/x-loumy-asset";
export const FOLDER_DRAG_TYPE = "application/x-loumy-folder";
// Job-Eintrag der §11-Jobs-Liste (Generate-Sidebar): Drop verschiebt ALLE Ergebnis-Assets des Jobs.
export const JOB_DRAG_TYPE = "application/x-loumy-job";

/** `types` einer DragEvent gegen einen unserer Payload-Typen prüfen (case-tolerant). */
export function hasDragType(types: readonly string[], type: string): boolean {
  return types.some((t) => t.toLowerCase() === type);
}

/**
 * Hat der Drag das Drop-Ziel WIRKLICH verlassen? `dragleave` feuert auch beim Übergang
 * auf Kind-Elemente derselben Row (Icon/Text/Button) — dann zeigt `relatedTarget` ins
 * Ziel hinein und das Highlight darf NICHT gelöscht werden (Codex-P2: Flacker).
 * `relatedTarget` null (Fenster verlassen) ⇒ verlassen.
 */
export function leftDropTarget(
  currentTarget: { contains(node: Node | null): boolean },
  relatedTarget: EventTarget | null,
): boolean {
  return !currentTarget.contains(relatedTarget as Node | null);
}
